<!--
  Bereich anlegen (TIM-73).

  Bewusst schlank: Art, Name, Zeitraum. Farbe und Status kommen vom
  Hauptprojekt, weil ein Bereich zu ihm gehoert - wer sie abweichend will,
  aendert sie hinterher in der Projektmaske.

  "Bereich" statt "Unterprojekt" in der Oberflaeche: eine Unterkunft ist kein
  Projekt. Technisch bleibt es eines (parent_id), und genau deshalb erbt es
  alles, was ein Projekt kann.
-->
<template>
  <div v-if="props.open" class="sp-overlay" @click.self="emit('cancel')">
    <div class="sp-dialog" role="dialog" aria-modal="true" :aria-label="titel">
      <div class="sp-head">
        <h2>{{ titel }}</h2>
        <button class="sp-close" aria-label="Schließen" @click="emit('cancel')">
          <X :size="18" />
        </button>
      </div>

      <div class="sp-body">
        <label class="sp-field">
          <span>Art</span>
          <select v-model="kategorie">
            <option v-for="(label, key) in CATEGORIES" :key="key" :value="key">{{ label }}</option>
          </select>
        </label>

        <label class="sp-field">
          <span>Name</span>
          <input ref="nameRef" v-model="name" type="text" :placeholder="platzhalter" @keyup.enter="speichern" />
        </label>

        <p v-if="fehler" class="sp-error">{{ fehler }}</p>
        <p v-else class="sp-hint">
          Zeitraum, Farbe und Status übernimmt der Bereich von
          {{ props.project?.name }}<template v-if="zeitraum"> ({{ zeitraum }})</template>.
          Ein Leistungsbereich erbt auch dessen Pausen.
        </p>
      </div>

      <div class="sp-foot">
        <Button variant="outline" @click="emit('cancel')">Abbrechen</Button>
        <Button class="bg-green-500 hover:bg-green-600 text-white"
                :disabled="laeuft" @click="speichern">
          {{ laeuft ? 'Wird angelegt …' : 'Anlegen' }}
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { X } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { toGerman } from '@/utils/dateUtils'
import { CATEGORIES, sendRequest } from './planner.js'

const props = defineProps({
  open: { type: Boolean, required: true },
  project: { type: Object, default: null },
})

const emit = defineEmits(['cancel', 'created'])

const kategorie = ref(Object.keys(CATEGORIES)[0])
const name = ref('')
const fehler = ref('')
const laeuft = ref(false)
const nameRef = ref(null)

const titel = computed(() => `Neuer Bereich in ${props.project?.name ?? ''}`)
const platzhalter = computed(() =>
  kategorie.value === 'accommodation' ? 'z. B. Hotel Nord' : 'z. B. Rohbau')

const zeitraum = computed(() => {
  const p = props.project
  return p?.start && p?.end ? `${toGerman(p.start)} – ${toGerman(p.end)}` : ''
})

watch(() => props.open, (offen) => {
  if (!offen) return
  kategorie.value = Object.keys(CATEGORIES)[0]
  name.value = ''
  fehler.value = ''
  laeuft.value = false
  nextTick(() => nameRef.value?.focus())
})

function speichern() {
  if (laeuft.value) return
  fehler.value = ''

  if (!name.value.trim()) {
    fehler.value = 'Bitte einen Namen angeben.'
    return
  }
  laeuft.value = true
  sendRequest('/projects.json', {
    project: {
      name: name.value.trim(),
      description: `Bereich von ${props.project.name}`,
      status: props.project.status,
      color: props.project.color,
      // Ein Bereich laeuft, solange sein Hauptprojekt laeuft. Das Modell zieht
      // den Zeitraum ohnehin nach; was hier steht, ist nur die erste Fassung.
      // Fachlich koennte ein Bereich einen eigenen Zeitraum haben - technisch
      // ist er ja ein Projekt -, aber bisher braucht das niemand, und ein Feld
      // weniger heisst eine Entscheidung weniger beim Anlegen.
      start: props.project.start,
      end: props.project.end,
      parent_id: props.project.id,
      category: kategorie.value,
    },
  },
  (antwort) => {
    laeuft.value = false
    emit('created', antwort)
  },
  (antwort) => {
    laeuft.value = false
    // Der Server meldet Validierungsfehler als {base: [...]} oder je Feld.
    const texte = antwort && typeof antwort === 'object' ? Object.values(antwort).flat() : []
    fehler.value = texte.length ? texte.join(' ') : 'Der Bereich konnte nicht angelegt werden.'
  })
}
</script>

<style scoped>
.sp-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
}

.sp-dialog {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  width: min(28rem, calc(100vw - 2rem));
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sp-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.sp-head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
}

.sp-close {
  background: none;
  border: 0;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  padding: 2px;
}
.sp-close:hover { color: #111827; }

.sp-body {
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  overflow-y: auto;
}

.sp-field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.85rem;
}

.sp-field > span {
  color: #4b5563;
}

.sp-field select,
.sp-field input[type="text"] {
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 0.35rem 0.5rem;
  font-size: 0.9rem;
  background: #fff;
}

.sp-field select:focus,
.sp-field input[type="text"]:focus {
  outline: 2px solid #86b7dc;
  outline-offset: 0;
  border-color: #86b7dc;
}

.sp-hint {
  margin: 0;
  font-size: 0.8rem;
  color: #6b7280;
}

.sp-error {
  margin: 0;
  font-size: 0.85rem;
  color: #9c4221;
}

.sp-foot {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 0.9rem 1.25rem;
  border-top: 1px solid #e5e7eb;
}
</style>
